import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import Popover from '@rsos/components/capstone/base/Popover';
import {
  ExternalLinkBox,
  PopoverBox,
  PopoverText,
  CARD_BORDER_WIDTH,
  CARD_CONTENT_HEIGHT,
} from './ExternalLinkCard.styles';
import ExternalLinkCardContent from './ExternalLinkCardContent';

// Popover is displayed 8 pixels above the component.
const POPOVER_GAP = 8;

const POPOVER_POSITION =
  (CARD_CONTENT_HEIGHT / 2 + CARD_BORDER_WIDTH + POPOVER_GAP) * -1;

/**
 * Nested component for displaying the individual external link card in ExternalLinkCards.js.
 * @param {Object} data - External link card data.
 * @param {Boolean} isDisabled - Is the external link card disabled.
 * @param {Function} onClick - On click callback handler.
 */
const ExternalLinkCard = ({
  data = null,
  isDisabled = false,
  onClick = () => {},
}) => {
  const popoverRef = useRef();

  const [popoverBoundingClient, setPopoverBoundingClient] = useState(null);

  const [isPopoverVisible, setIsPopoverVisible] = useState(false);

  const popoverPosition = useMemo(() => {
    const position = popoverBoundingClient?.height
      ? -popoverBoundingClient.height
      : 0;

    return position + POPOVER_POSITION;
  }, [popoverBoundingClient]);

  const handleHover = isVisible => setIsPopoverVisible(isVisible);

  const handleMouseEnter = () => handleHover(true);

  const handleMouseLeave = () => handleHover(false);

  const handleClick = useCallback(() => {
    if (isDisabled) {
      return;
    }

    onClick(data);
  }, [data, isDisabled, onClick]);

  useEffect(() => {
    const popoverRect = popoverRef?.current?.getBoundingClientRect();

    if (popoverRect && popoverRect.height !== popoverBoundingClient?.height) {
      setPopoverBoundingClient(popoverRect);
    }
  }, [isPopoverVisible, popoverBoundingClient]);

  if (!data) return null;

  return (
    <ExternalLinkBox
      data-name={
        isDisabled ? 'external-link-card-disabled' : 'external-link-card'
      }
      isDisabled={isDisabled}
      onClick={handleClick}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onFocus={handleMouseEnter}
      onBlur={handleMouseLeave}
      role="button"
      tabIndex={0}
    >
      <Popover
        cssDisplayProperty="block"
        data-name="external-link-popover"
        isOpen={isPopoverVisible}
        popoverID="external-link-popover"
        trigger={
          <ExternalLinkCardContent
            title={data.call_to_action}
            subtitle={data.display_name_short}
          />
        }
        useCenterXOffset
        useCustomOffset
        yOffset={popoverPosition}
      >
        <PopoverBox ref={popoverRef}>
          <PopoverText data-name="external-link-description">
            {data.description}
          </PopoverText>
        </PopoverBox>
      </Popover>
    </ExternalLinkBox>
  );
};

export default ExternalLinkCard;
